import { getItem, setItem } from '@/db/services/application_session_service';
import db from '@/db/db';

// UUID v4 format validation
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Singleton promise to prevent concurrent UUID generation within a single tab
let pendingUUID: Promise<string> | null = null;

/**
 * Get or generate stable client UUID for this browser/device
 *
 * Privacy Note:
 * This UUID is a persistent device identifier stored in IndexedDB and sent to the server
 * with each document sync. It's used exclusively for CRDT conflict resolution (deterministic
 * tie-breaking) and does not contain any personally identifiable information. The UUID is
 * generated using crypto.randomUUID() and persists across browser sessions.
 *
 * The UUID is necessary for correct CRDT behavior - it ensures that when two clients edit
 * the same field at the exact same millisecond, the conflict is resolved deterministically
 * and consistently across all clients.
 *
 * Stored in applicationSession table (uses existing Dexie storage)
 */
export async function getClientUUID(): Promise<string> {
  // If a UUID generation is already in flight, reuse its result
  if (pendingUUID) {
    return pendingUUID;
  }

  pendingUUID = resolveClientUUID();

  try {
    return await pendingUUID;
  } finally {
    pendingUUID = null;
  }
}

async function resolveClientUUID(): Promise<string> {
  const stored = await getItem<string>('client_uuid');

  // Validate stored UUID format
  if (stored && UUID_REGEX.test(stored)) {
    return stored;
  }

  // If stored UUID is invalid, warn and regenerate
  if (stored && !UUID_REGEX.test(stored)) {
    console.warn('[CRDT] Invalid client UUID format detected, regenerating', { stored });
  }

  // Check for crypto.randomUUID support
  if (!crypto.randomUUID) {
    throw new Error('crypto.randomUUID() not supported - CRDT requires secure UUID generation');
  }

  // Generate UUID, then use a transaction to prevent multi-tab race condition.
  // Two tabs opened simultaneously could each generate different UUIDs;
  // the transaction ensures only the first writer's UUID is kept.
  const uuid = crypto.randomUUID();
  return await db.transaction('rw', db.applicationSession, async () => {
    // Re-check inside transaction in case another tab wrote first
    const existing = await db.applicationSession.get('client_uuid');
    if (existing?.value && UUID_REGEX.test(existing.value as string)) {
      return existing.value as string;
    }
    await setItem('client_uuid', uuid);
    return uuid;
  });
}
